import { screenToBoard } from '@kanvas/board-core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  deleteSelection,
  duplicateSelection,
  groupSelection,
  reorderSelection,
  selectAll,
  setLocked,
  ungroupSelection,
  unlockAll,
  zoomToSelection,
  type ActionContext,
} from '../actions/actions';
import { copySelection, cutSelection, paste } from '../actions/clipboard';
import { useEditor } from '../state/store';
import { Icon, type IconName } from './icons';

interface MenuEntry {
  label: string;
  icon?: IconName;
  shortcut?: string;
  run: () => void;
  disabled?: boolean;
}

export function ContextMenu({ ctx }: { ctx: ActionContext }) {
  const menu = useEditor((s) => s.contextMenu);
  const selection = useEditor((s) => s.selection);
  const ref = useRef<HTMLDivElement>(null);
  /** Position after being pulled back inside the viewport, measured from the DOM. */
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (!menu) return;
    const close = () => useEditor.getState().closeContextMenu();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // A pointerdown anywhere dismisses, including on the canvas behind it.
    // So does anything that moves the board under it — the menu's position
    // and its "paste here" point would otherwise be stale.
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', close, { passive: true });
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('wheel', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
    };
  }, [menu]);

  // Measure the real menu rather than assuming a size: a canvas menu is far
  // shorter than a selection menu, and guessing put the short one too high.
  useLayoutEffect(() => {
    if (!menu) {
      setPos(null);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const edge = 8;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setPos({
      left: Math.max(edge, Math.min(menu.x, window.innerWidth - w - edge)),
      top: Math.max(edge, Math.min(menu.y, window.innerHeight - h - edge)),
    });
    el.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [menu]);

  if (!menu) return null;

  // Arrow keys walk the items, as a native menu does; focus is moved into the
  // menu on open, so this is what makes it usable without a mouse. Handled on
  // the menu itself and stopped there, so the same keys do not also nudge the
  // selection behind it.
  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }
    const items = [
      ...(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ??
        []),
    ];
    if (items.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    const at = items.findIndex((el) => el === document.activeElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? items.length - 1
          : e.key === 'ArrowDown'
            ? (at + 1) % items.length
            : (at - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const nodes = ctx.doc.getMany(selection);
  const hasSelection = nodes.length > 0;
  const hasGroup = nodes.some((n) => n.type === 'group');
  const locked = nodes.some((n) => n.locked);

  const boardPoint = screenToBoard(useEditor.getState().viewport, {
    x: menu.x,
    y: menu.y,
  });

  const groups: MenuEntry[][] = hasSelection
    ? [
        [
          { label: 'Вырезать', icon: 'trash', shortcut: 'Ctrl X', run: () => void cutSelection(ctx) },
          { label: 'Копировать', icon: 'copy', shortcut: 'Ctrl C', run: () => void copySelection(ctx) },
          {
            label: 'Дублировать',
            icon: 'copy',
            shortcut: 'Ctrl D',
            run: () => duplicateSelection(ctx),
          },
        ],
        [
          {
            label: 'На передний план',
            icon: 'toFront',
            shortcut: ']',
            run: () => reorderSelection(ctx, 'front'),
          },
          {
            label: 'На задний план',
            icon: 'toBack',
            shortcut: '[',
            run: () => reorderSelection(ctx, 'back'),
          },
        ],
        [
          {
            label: 'Сгруппировать',
            icon: 'group',
            shortcut: 'Ctrl G',
            disabled: nodes.length < 2,
            run: () => groupSelection(ctx),
          },
          {
            label: 'Разгруппировать',
            icon: 'ungroup',
            shortcut: 'Ctrl Shift G',
            disabled: !hasGroup,
            run: () => ungroupSelection(ctx),
          },
          {
            label: locked ? 'Разблокировать' : 'Заблокировать',
            icon: locked ? 'unlock' : 'lock',
            run: () => setLocked(ctx, !locked),
          },
        ],
        [
          { label: 'Приблизить к выделению', icon: 'fit', shortcut: 'Shift 2', run: () => zoomToSelection(ctx) },
          { label: 'Удалить', icon: 'trash', shortcut: 'Del', run: () => deleteSelection(ctx) },
        ],
      ]
    : [
        [
          {
            label: 'Вставить здесь',
            icon: 'copy',
            shortcut: 'Ctrl V',
            run: () => void paste(ctx, boardPoint),
          },
          { label: 'Выбрать всё', shortcut: 'Ctrl A', run: () => selectAll(ctx) },
        ],
        [
          { label: 'Разблокировать всё', icon: 'unlock', run: () => unlockAll(ctx) },
        ],
      ];

  // Keep the menu on screen when opened near an edge. Until measured it sits
  // at the pointer, invisible, so the first painted frame is already placed.
  const style: React.CSSProperties = pos
    ? { left: pos.left, top: pos.top }
    : { left: menu.x, top: menu.y, visibility: 'hidden' };

  return (
    <div
      className="context-menu"
      ref={ref}
      style={style}
      role="menu"
      aria-label={hasSelection ? 'Действия с выделением' : 'Действия с доской'}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={onMenuKeyDown}
    >
      {groups.map((group, i) => (
        <div className="context-group" key={i}>
          {group.map((entry) => (
            <button
              key={entry.label}
              type="button"
              role="menuitem"
              className="menu-item"
              disabled={entry.disabled}
              onClick={() => {
                entry.run();
                useEditor.getState().closeContextMenu();
              }}
            >
              {entry.icon ? <Icon name={entry.icon} size={16} /> : <span className="menu-spacer" />}
              <span className="menu-label">{entry.label}</span>
              {entry.shortcut && <kbd>{entry.shortcut}</kbd>}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
